/** A point in the composition's space. */
export type Point = { x: number; y: number };

/** A point along a flight: a straight line from `from` to `to` lifted into an arc by `lift` at its middle. */
export function arc(from: Point, to: Point, lift: number, k: number): Point {
  return { x: from.x + (to.x - from.x) * k, y: from.y + (to.y - from.y) * k - lift * Math.sin(Math.PI * k) };
}

/**
 * A point along a light's flight: the straight line from `from` to `to`, bowed out by `bow` at its middle across it, to
 * its upper side — a level flight arches over the top, an upright one swings out sideways (to its left when exactly
 * upright), and none doubles back on itself.
 */
export function bowed(from: Point, to: Point, bow: number, k: number): Point {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;
  // The chord's normal on its upper side (y grows downwards).
  let nx = dy / length;
  let ny = -dx / length;
  if (ny > 0 || (ny === 0 && nx > 0)) {
    nx = -nx;
    ny = -ny;
  }
  const out = bow * Math.sin(Math.PI * k);
  return { x: from.x + dx * k + nx * out, y: from.y + dy * k + ny * out };
}

/**
 * How far a flight from `from` to `to` may bow out: `bow`, at most what keeps its tightest turn (the bow's middle)
 * `minRadius` round — a light is a rope as wide as its texture, and a tighter turn folds its inner side over.
 */
export function bowFor(from: Point, to: Point, bow: number, minRadius: number): number {
  const chord = Math.hypot(to.x - from.x, to.y - from.y);
  return Math.min(bow, (chord * chord) / (Math.PI * Math.PI * Math.max(1e-6, minRadius)));
}

/** A pot flight's shape (`potArc`): its loop over the pot, its straight drop into it, how much its rise bows. */
export interface PotArcShape {
  /** The loop's radius: the tightest turn of the flight. */
  radius: number;
  /** The straight drop into the pot at the end. */
  drop: number;
  /** The rise's bow, as a share of its chord (an arc turning the loop's way). */
  bow: number;
}

/**
 * A light rising into a pot (`to` above `from`), as points `step` apart along it: a gentle arc up (bowed `bow` of its
 * chord), over the pot on a turn of `radius` and straight down the last `drop` into it — it lands from above. The loop
 * is on the coin's side, or for a coin nearly under its pot, on the side facing the composition's middle (out from an
 * edge pot it would leave the screen). Its every turn is `radius` round or wider. Null when `from` is too near the loop
 * to rise into it.
 */
export function potArc(from: Point, to: Point, shape: PotArcShape, step = 4): Point[] | null {
  const { radius: r, drop, bow } = shape;
  const side = loopSide(from, to, r);
  const centre = { x: to.x + side * r, y: to.y - drop };
  // Round the loop: over its top to its point beside the pot, heading down there.
  const endAngle = side > 0 ? Math.PI : 0;
  const turn = side > 0 ? -1 : 1;
  const away = Math.hypot(from.x - centre.x, from.y - centre.y);
  if (away <= r * 1.05) return null;
  const onLoop = (angle: number): Point => ({ x: centre.x + r * Math.cos(angle), y: centre.y + r * Math.sin(angle) });
  const heading = (angle: number): Point => ({ x: -Math.sin(angle) * turn, y: Math.cos(angle) * turn });
  // The tangent from `from` that goes on round the loop.
  const base = Math.atan2(from.y - centre.y, from.x - centre.x);
  const spread = Math.acos(r / away);
  const tangent = [base + spread, base - spread]
    .map((angle) => {
      const at = onLoop(angle);
      const along = heading(angle);
      return { angle, fit: ((at.x - from.x) * along.x + (at.y - from.y) * along.y) / Math.hypot(at.x - from.x, at.y - from.y) };
    })
    .sort((a, b) => b.fit - a.fit)[0]!.angle;
  // The rise meets the loop further on, an arc from `from` tangent to the loop there, bowed `bow` of its chord.
  const rise = (on: number): { angle: number; joint: Point; centre: Point; radius: number; bowed: number } | null => {
    const angle = tangent + turn * on;
    const joint = onLoop(angle);
    const inward = { x: (centre.x - joint.x) / r, y: (centre.y - joint.y) / r };
    const reach = (from.x - joint.x) * inward.x + (from.y - joint.y) * inward.y;
    if (reach <= 1e-6) return null;
    const chord = Math.hypot(from.x - joint.x, from.y - joint.y);
    const radius = (chord * chord) / (2 * reach);
    const sagitta = radius - Math.sqrt(Math.max(0, radius * radius - (chord / 2) ** 2));
    return { angle, joint, centre: { x: joint.x + inward.x * radius, y: joint.y + inward.y * radius }, radius, bowed: sagitta / chord };
  };
  let low = 0;
  let high = Math.PI / 2;
  for (let i = 0; i < 50; i++) {
    const mid = (low + high) / 2;
    const at = rise(mid);
    if (!at || at.bowed < bow) low = mid;
    else high = mid;
  }
  const up = rise(high);
  if (!up) return null;
  const points: Point[] = [];
  // The rise: round its centre from `from` to the joint, turning the loop's way.
  const a0 = Math.atan2(from.y - up.centre.y, from.x - up.centre.x);
  const a1 = Math.atan2(up.joint.y - up.centre.y, up.joint.x - up.centre.x);
  const riseSweep = wrap(turn > 0 ? a1 - a0 : a0 - a1);
  const riseSteps = Math.max(2, Math.ceil((up.radius * riseSweep) / step));
  for (let i = 0; i < riseSteps; i++) {
    const angle = a0 + turn * riseSweep * (i / riseSteps);
    points.push({ x: up.centre.x + up.radius * Math.cos(angle), y: up.centre.y + up.radius * Math.sin(angle) });
  }
  // The loop: from the joint over the top to beside the pot.
  const loopSweep = wrap(turn > 0 ? endAngle - up.angle : up.angle - endAngle);
  const loopSteps = Math.max(2, Math.ceil((r * loopSweep) / step));
  for (let i = 0; i < loopSteps; i++) points.push(onLoop(up.angle + turn * loopSweep * (i / loopSteps)));
  // The drop, straight down into the pot.
  const dropSteps = Math.max(1, Math.ceil(drop / step));
  for (let i = 0; i <= dropSteps; i++) points.push({ x: to.x, y: to.y - drop + drop * (i / dropSteps) });
  return points;
}

/** An angle as a turn's sweep, 0 to a full turn. */
function wrap(angle: number): number {
  const turn = 2 * Math.PI;
  return ((angle % turn) + turn) % turn;
}

/** The side of a pot its light loops round: the coin's, or for a coin nearly under it, the composition's middle's. */
function loopSide(from: Point, to: Point, radius: number): 1 | -1 {
  const across = from.x - to.x;
  if (Math.abs(across) > radius) return across > 0 ? 1 : -1;
  if (Math.abs(to.x) > radius) return to.x < 0 ? 1 : -1;
  return across >= 0 ? 1 : -1;
}

/** How a light paces its flight (`ArcPath.timing`). */
export interface FlightPace {
  /** Its speed, design units a second, where its path is straight or curves at least `fullSpeedRadius` round. */
  speed: number;
  /** A tighter curve is flown slower: by the cube root of its radius over this one. */
  fullSpeedRadius: number;
  /**
   * How far along either side of a point its curve is measured (its path's turning over that stretch); its pace is
   * then averaged over half that either side, so it eases into a turn without a jolt.
   */
  smoothing: number;
}

/** A flight's timing: how long it takes, and how far along its path it is at a share of that time. */
export interface FlightTiming {
  readonly seconds: number;
  distanceAt(share: number): number;
}

/** The turn from heading `from` to heading `to`, between a half turn either way. */
function turnBetween(from: number, to: number): number {
  return wrap(to - from + Math.PI) - Math.PI;
}

/**
 * A light's path from `from` to `to` (by its length): into a pot above it, `potArc` — it lands from above; anywhere
 * else (a pot's light down to the meter or a cell), the straight line bowed `lift` to its upper side (`bowed`, the bow
 * eased on a short flight so no turn is tighter than `shape.radius`).
 */
export function lightPath(from: Point, to: Point, lift: number, shape: PotArcShape): ArcPath {
  const rising = to.y < from.y ? potArc(from, to, shape) : null;
  if (rising) return new ArcPath(rising);
  const bow = bowFor(from, to, lift, shape.radius);
  return ArcPath.sample((k) => bowed(from, to, bow, k));
}

/**
 * A flight's path by its length, through its points in order: the point at a distance along it — and before its start
 * or past its end, straight on along its line there, so what trails a point near an end follows the path — and its
 * timing at a pace.
 */
export class ArcPath {
  /** The path's length. */
  public readonly length: number;
  private readonly points: readonly Point[];
  private readonly distances: number[] = [];
  private readonly startHeading: number;
  private readonly endHeading: number;

  public constructor(points: readonly Point[]) {
    this.points = points;
    let distance = 0;
    points.forEach((point, i) => {
      const last = points[i - 1];
      if (last) distance += Math.hypot(point.x - last.x, point.y - last.y);
      this.distances.push(distance);
    });
    this.length = distance;
    const heading = (a: Point, b: Point): number => Math.atan2(b.y - a.y, b.x - a.x);
    this.startHeading = heading(points[0]!, points[1]!);
    this.endHeading = heading(points[points.length - 2]!, points[points.length - 1]!);
  }

  /** A path through `at`'s points at `samples` equal steps of its progress. */
  public static sample(at: (k: number) => Point, samples = 128): ArcPath {
    return new ArcPath(Array.from({ length: samples + 1 }, (_, i) => at(i / samples)));
  }

  /**
   * The path flown at `pace`: at its speed where it is straight or gently curved, slower round a tighter curve — the
   * speed there the cube root of the curve's radius (the two-thirds power law of motion: the eye reads such a motion as
   * even, and one at a steady speed as speeding up in its turns). The curve at a point is the path's turning over
   * `pace.smoothing` either side of it, and the pace (the time a unit of the path takes) averaged over half that, so
   * the light eases into a turn and out of it. Its points `step` apart.
   */
  public timing(pace: FlightPace, step = 4): FlightTiming {
    if (!(this.length > 0)) return { seconds: 0, distanceAt: () => 0 };
    const count = Math.max(2, Math.ceil(this.length / step));
    const gap = this.length / count;
    const points = Array.from({ length: count + 1 }, (_, i) => this.pointAt(i * gap));
    // Each step's heading, unwrapped along the path: the turning between two steps is the difference of theirs.
    const headings: number[] = [];
    for (let i = 0; i < count; i++) {
      const [a, b] = [points[i]!, points[i + 1]!];
      const heading = Math.atan2(b.y - a.y, b.x - a.x);
      const last = headings[i - 1];
      headings.push(last === undefined ? heading : last + turnBetween(last, heading));
    }
    const reach = Math.max(1, Math.round(pace.smoothing / gap));
    const paces = points.map((_, i) => {
      const [first, last] = [Math.max(0, i - reach), Math.min(count - 1, i + reach)];
      const curve = Math.abs(headings[last]! - headings[first]!) / ((last - first) * gap);
      return 1 / (pace.speed * Math.min(1, Math.cbrt(1 / (pace.fullSpeedRadius * curve))));
    });
    const ease = Math.max(1, Math.round(reach / 2));
    const eased = paces.map((_, i) => {
      const near = paces.slice(Math.max(0, i - ease), i + ease + 1);
      return near.reduce((sum, value) => sum + value, 0) / near.length;
    });
    // The time at each point: each step at the mean of its ends' paces.
    const times = [0];
    for (let i = 1; i <= count; i++) times.push(times[i - 1]! + (gap * (eased[i - 1]! + eased[i]!)) / 2);
    const seconds = times[count]!;
    return {
      seconds,
      distanceAt: (share) => {
        const time = Math.min(1, Math.max(0, share)) * seconds;
        let low = 0;
        let high = count;
        while (high - low > 1) {
          const mid = (low + high) >> 1;
          if (times[mid]! <= time) low = mid;
          else high = mid;
        }
        const span = times[high]! - times[low]!;
        return (low + (span > 0 ? (time - times[low]!) / span : 0)) * gap;
      },
    };
  }

  /** The point `distance` along the path: before its start or past its end, straight on along its line there. */
  public pointAt(distance: number): Point {
    const first = this.points[0]!;
    const last = this.points[this.points.length - 1]!;
    if (distance <= 0) return { x: first.x + Math.cos(this.startHeading) * distance, y: first.y + Math.sin(this.startHeading) * distance };
    if (distance >= this.length) {
      const beyond = distance - this.length;
      return { x: last.x + Math.cos(this.endHeading) * beyond, y: last.y + Math.sin(this.endHeading) * beyond };
    }
    // The point the distance falls after (the distances rise), then between it and the next.
    let low = 0;
    let high = this.distances.length - 1;
    while (high - low > 1) {
      const mid = (low + high) >> 1;
      if (this.distances[mid]! <= distance) low = mid;
      else high = mid;
    }
    const span = this.distances[high]! - this.distances[low]!;
    const t = span > 0 ? (distance - this.distances[low]!) / span : 0;
    const a = this.points[low]!;
    const b = this.points[high]!;
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
  }
}
